import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Key, ArrowLeft, Webhook } from "lucide-react";

const AdminAPISettings = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [testWebhookUrl, setTestWebhookUrl] = useState("");
  const [productionWebhookUrl, setProductionWebhookUrl] = useState("");
  const [testingWebhook, setTestingWebhook] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('api_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['api_mode', 'lead_webhook_enabled', 'lead_webhook_url_test', 'lead_webhook_url_production']);

      if (error) throw error;

      const settings = data.reduce((acc, { setting_key, setting_value }) => {
        acc[setting_key] = setting_value;
        return acc;
      }, {} as Record<string, string>);

      setIsLiveMode(settings.api_mode === 'live');
      setWebhookEnabled(settings.lead_webhook_enabled === 'true');
      setTestWebhookUrl(settings.lead_webhook_url_test || '');
      setProductionWebhookUrl(settings.lead_webhook_url_production || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = async (checked: boolean) => {
    setSaving(true);
    const newMode = checked ? 'live' : 'test';
    
    try {
      const { error } = await supabase
        .from('api_settings')
        .update({ setting_value: newMode })
        .eq('setting_key', 'api_mode');

      if (error) throw error;

      setIsLiveMode(checked);
      toast({
        title: "Settings Updated",
        description: `API mode set to ${newMode.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Error updating API mode:', error);
      toast({
        title: "Error",
        description: "Failed to update API settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWebhook = async (checked: boolean) => {
    setSaving(true);
    
    try {
      const { error } = await supabase
        .from('api_settings')
        .update({ setting_value: checked.toString() })
        .eq('setting_key', 'lead_webhook_enabled');

      if (error) throw error;

      setWebhookEnabled(checked);
      toast({
        title: "Settings Updated",
        description: `Lead webhook ${checked ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      console.error('Error updating webhook settings:', error);
      toast({
        title: "Error",
        description: "Failed to update webhook settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const validateWebhookUrl = (url: string, isProduction: boolean): string | null => {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      
      // Block internal IPs and localhost
      const forbiddenHosts = ['localhost', '127.0.0.1', '0.0.0.0'];
      if (forbiddenHosts.includes(parsed.hostname)) {
        return "Cannot use localhost or internal IP addresses";
      }
      
      if (parsed.hostname.match(/^10\./) ||
          parsed.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./) ||
          parsed.hostname.match(/^192\.168\./)) {
        return "Cannot use internal IP addresses";
      }
      
      // Production must use HTTPS
      if (isProduction && parsed.protocol !== 'https:') {
        return "Production webhooks must use HTTPS";
      }
      
      // Test mode can use HTTP
      if (!isProduction && parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        return "Invalid protocol. Use HTTP or HTTPS";
      }
      
      return null;
    } catch {
      return "Invalid URL format";
    }
  };

  const handleSaveWebhookUrls = async () => {
    setSaving(true);
    
    try {
      // Validate URLs if provided
      if (testWebhookUrl) {
        const testError = validateWebhookUrl(testWebhookUrl, false);
        if (testError) {
          toast({
            title: "Validation Error",
            description: `Test URL: ${testError}`,
            variant: "destructive"
          });
          setSaving(false);
          return;
        }
      }

      if (productionWebhookUrl) {
        const prodError = validateWebhookUrl(productionWebhookUrl, true);
        if (prodError) {
          toast({
            title: "Validation Error",
            description: `Production URL: ${prodError}`,
            variant: "destructive"
          });
          setSaving(false);
          return;
        }
      }

      const updates = [
        { setting_key: 'lead_webhook_url_test', setting_value: testWebhookUrl },
        { setting_key: 'lead_webhook_url_production', setting_value: productionWebhookUrl }
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('api_settings')
          .update({ setting_value: update.setting_value })
          .eq('setting_key', update.setting_key);

        if (error) throw error;
      }

      toast({
        title: "Webhook URLs Updated",
        description: "Webhook configuration saved successfully",
      });
    } catch (error) {
      console.error('Error updating webhook URLs:', error);
      toast({
        title: "Error",
        description: "Failed to update webhook URLs",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('test-lead-webhook');

      if (error) {
        console.error('Edge function error:', error);
        toast({
          title: "Test Failed",
          description: error.message || "Failed to invoke test webhook function",
          variant: "destructive",
        });
        return;
      }

      if (!data) {
        toast({
          title: "Test Failed",
          description: "No response from webhook test",
          variant: "destructive",
        });
        return;
      }

      if (data.success) {
        toast({
          title: "Webhook Test Successful ✓",
          description: `Status: ${data.status} | Mode: ${data.mode} | Duration: ${data.duration_ms}ms${data.response_body ? '\nResponse: ' + (typeof data.response_body === 'string' ? data.response_body.substring(0, 100) : JSON.stringify(data.response_body).substring(0, 100)) : ''}`,
        });
      } else {
        toast({
          title: "Webhook Test Failed",
          description: data.error || `HTTP ${data.status}: ${data.response_body ? (typeof data.response_body === 'string' ? data.response_body.substring(0, 200) : JSON.stringify(data.response_body).substring(0, 200)) : 'No response body'}`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast({
        title: "Webhook Test Error",
        description: error instanceof Error ? error.message : "Unexpected error during webhook test",
        variant: "destructive",
      });
    } finally {
      setTestingWebhook(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">API Settings</h1>
          <p className="text-muted-foreground">
            Manage Stripe and EasyPost API configurations
          </p>
        </div>

        <div className="grid gap-6 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Mode
              </CardTitle>
              <CardDescription>
                Toggle between test and live API keys for Stripe and EasyPost
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="api-mode" className="text-base font-medium">
                    {isLiveMode ? 'Live Mode' : 'Test Mode'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {isLiveMode 
                      ? 'Using production API keys for real transactions'
                      : 'Using test API keys for development and testing'
                    }
                  </p>
                </div>
                <Switch
                  id="api-mode"
                  checked={isLiveMode}
                  onCheckedChange={handleToggleMode}
                  disabled={saving}
                />
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium">Current Configuration</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Stripe Key:</span>
                    <span className="font-mono">
                      {isLiveMode ? 'STRIPE_SECRET_KEY' : 'STRIPE_TEST_KEY'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">EasyPost Key:</span>
                    <span className="font-mono">
                      {isLiveMode ? 'EASYPOST_API_KEY' : 'EASYPOST_TEST_KEY'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Note:</strong> Make sure both test and live API keys are configured in your backend secrets before toggling modes.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>API Key Management</CardTitle>
              <CardDescription>
                API keys are securely stored in backend secrets
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Required Secrets:</h4>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>STRIPE_SECRET_KEY (Live)</li>
                  <li>STRIPE_TEST_KEY (Test)</li>
                  <li>EASYPOST_API_KEY (Live)</li>
                  <li>EASYPOST_TEST_KEY (Test)</li>
                </ul>
              </div>
              <p className="text-sm text-muted-foreground">
                Contact your administrator to update API keys in the backend configuration.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Webhook className="h-5 w-5" />
                Lead Webhook Configuration
              </CardTitle>
              <CardDescription>
                Configure webhooks to send lead data to external systems (e.g., n8n)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="webhook-enabled" className="text-base font-medium">
                    {webhookEnabled ? 'Webhook Enabled' : 'Webhook Disabled'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {webhookEnabled 
                      ? 'Lead data will be sent to configured webhook'
                      : 'Webhook notifications are disabled'
                    }
                  </p>
                </div>
                <Switch
                  id="webhook-enabled"
                  checked={webhookEnabled}
                  onCheckedChange={handleToggleWebhook}
                  disabled={saving}
                />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="test-webhook">Test Webhook URL</Label>
                  <Input
                    id="test-webhook"
                    type="url"
                    placeholder="https://your-n8n-instance.com/webhook/test"
                    value={testWebhookUrl}
                    onChange={(e) => setTestWebhookUrl(e.target.value)}
                    disabled={saving}
                  />
                  <p className="text-xs text-muted-foreground">
                    Used when API is in Test Mode
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="production-webhook">Production Webhook URL</Label>
                  <Input
                    id="production-webhook"
                    type="url"
                    placeholder="https://your-n8n-instance.com/webhook/production"
                    value={productionWebhookUrl}
                    onChange={(e) => setProductionWebhookUrl(e.target.value)}
                    disabled={saving}
                  />
                  <p className="text-xs text-muted-foreground">
                    Used when API is in Live Mode
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveWebhookUrls}
                    disabled={saving}
                  >
                    Save Webhook URLs
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleTestWebhook}
                    disabled={testingWebhook || !webhookEnabled}
                  >
                    {testingWebhook ? 'Testing...' : 'Test Webhook'}
                  </Button>
                </div>
              </div>

              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium">Current Configuration</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status:</span>
                    <span className={webhookEnabled ? "text-green-600 dark:text-green-400" : "text-muted-foreground"}>
                      {webhookEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Active Webhook:</span>
                    <span className="font-mono text-xs">
                      {isLiveMode ? 'Production' : 'Test'}
                    </span>
                  </div>
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">URL:</span>
                    <span className="font-mono text-xs text-right break-all max-w-[60%]">
                      {isLiveMode 
                        ? (productionWebhookUrl || 'Not set') 
                        : (testWebhookUrl || 'Not set')
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-950/20">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Note:</strong> Webhook will automatically use the test URL in Test Mode and production URL in Live Mode. Make sure both URLs are configured before enabling.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminAPISettings;
